import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, Mail, Phone } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  navn: "",
  virksomhed: "",
  email: "",
  telefon: "",
  har_hjemmeside: "",
  besked: "",
};

export const FinalCTA = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Tak for din henvendelse! Vi svarer normalt inden for 1 hverdag.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Noget gik galt. Prøv igen, eller skriv til os direkte på e-mail.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-zinc-700 bg-zinc-800/60 px-4 py-3.5 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-white transition-colors duration-200";

  return (
    <section id="kontakt" className="py-20 sm:py-28 bg-zinc-900 text-white" data-testid="final-cta-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-20 items-start">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400 mb-4">
              Gratis forslag
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
              Klar til en hjemmeside, der matcher din virksomhed?
            </h2>
            <p className="mt-6 text-base sm:text-lg text-zinc-400 leading-relaxed max-w-md">
              Udfyld formularen, så vender vi tilbage med et gratis og uforpligtende
              forslag. Ingen salgstricks — bare en ærlig snak om, hvad der giver
              mening for dig.
            </p>
            <ul className="mt-8 space-y-3 text-sm text-zinc-400">
              <li>— Vi svarer normalt inden for 1 hverdag</li>
              <li>— Fast pris, før arbejdet begynder</li>
              <li>— Helt uforpligtende</li>
            </ul>
            <div className="mt-10 space-y-3">
              <a
                href="mailto:naveli.dk@gmail.com"
                data-testid="contact-email-link"
                className="flex items-center gap-3 text-sm text-zinc-300 hover:text-white transition-colors duration-200 w-fit"
              >
                <Mail className="w-4 h-4 text-zinc-500" aria-hidden="true" />
                naveli.dk@gmail.com
              </a>
              <a
                href="tel:+4522664605"
                data-testid="contact-phone-1-link"
                className="flex items-center gap-3 text-sm text-zinc-300 hover:text-white transition-colors duration-200 w-fit"
              >
                <Phone className="w-4 h-4 text-zinc-500" aria-hidden="true" />
                22 66 46 05
              </a>
              <a
                href="tel:+4542173209"
                data-testid="contact-phone-2-link"
                className="flex items-center gap-3 text-sm text-zinc-300 hover:text-white transition-colors duration-200 w-fit"
              >
                <Phone className="w-4 h-4 text-zinc-500" aria-hidden="true" />
                42 17 32 09
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <form onSubmit={submit} className="space-y-5" data-testid="lead-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="lead-navn" className="block text-sm font-medium text-zinc-300 mb-2">
                    Navn *
                  </label>
                  <input
                    id="lead-navn"
                    data-testid="lead-form-name-input"
                    required
                    value={form.navn}
                    onChange={update("navn")}
                    placeholder="f.eks. Thomas Hansen"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="lead-virksomhed" className="block text-sm font-medium text-zinc-300 mb-2">
                    Virksomhed
                  </label>
                  <input
                    id="lead-virksomhed"
                    data-testid="lead-form-company-input"
                    value={form.virksomhed}
                    onChange={update("virksomhed")}
                    placeholder="f.eks. Hansens Auto"
                    className={inputCls}
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="lead-email" className="block text-sm font-medium text-zinc-300 mb-2">
                    E-mail *
                  </label>
                  <input
                    id="lead-email"
                    type="email"
                    data-testid="lead-form-email-input"
                    required
                    value={form.email}
                    onChange={update("email")}
                    placeholder="dig@dinvirksomhed.dk"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="lead-telefon" className="block text-sm font-medium text-zinc-300 mb-2">
                    Telefonnummer
                  </label>
                  <input
                    id="lead-telefon"
                    type="tel"
                    data-testid="lead-form-phone-input"
                    value={form.telefon}
                    onChange={update("telefon")}
                    placeholder="12 34 56 78"
                    className={inputCls}
                  />
                </div>
              </div>
              <div>
                <label htmlFor="lead-hjemmeside" className="block text-sm font-medium text-zinc-300 mb-2">
                  Har du allerede en hjemmeside?
                </label>
                <select
                  id="lead-hjemmeside"
                  data-testid="lead-form-website-select"
                  value={form.har_hjemmeside}
                  onChange={update("har_hjemmeside")}
                  className={`${inputCls} ${form.har_hjemmeside ? "" : "text-zinc-500"}`}
                >
                  <option value="" disabled>
                    Vælg ...
                  </option>
                  <option value="Ja">Ja</option>
                  <option value="Nej">Nej</option>
                </select>
              </div>
              <div>
                <label htmlFor="lead-besked" className="block text-sm font-medium text-zinc-300 mb-2">
                  Fortæl kort, hvad du har brug for *
                </label>
                <textarea
                  id="lead-besked"
                  data-testid="lead-form-message-input"
                  required
                  rows={4}
                  value={form.besked}
                  onChange={update("besked")}
                  placeholder="f.eks. Vi har brug for en ny side med online tidsbestilling ..."
                  className={inputCls}
                />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="lead-form-submit-button"
                className="inline-flex items-center justify-center gap-2 bg-white text-zinc-900 font-medium rounded-full px-8 py-4 hover:bg-zinc-200 hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-60 disabled:translate-y-0"
              >
                {sending ? (
                  <>
                    Sender ...
                    <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                  </>
                ) : (
                  <>
                    Få mit gratis forslag
                    <ArrowRight className="w-4 h-4" aria-hidden="true" />
                  </>
                )}
              </button>
              <p className="text-sm text-zinc-500">Vi svarer normalt inden for 1 hverdag.</p>
              <p className="text-xs text-zinc-500 leading-relaxed">
                Når du sender formularen, behandler vi dine oplysninger som beskrevet
                i vores{" "}
                <Link
                  to="/privatlivspolitik"
                  data-testid="lead-form-privacy-link"
                  className="underline underline-offset-2 hover:text-zinc-300 transition-colors duration-200"
                >
                  privatlivspolitik
                </Link>
                .
              </p>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
